import { useState, useRef, useEffect } from 'react'
import { Send, Plus, Trash2, MessageSquare, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

interface Conversation {
  id: string
  title: string
  createdAt: Date
  updatedAt: Date
}

interface BackofficeChatProps {
  userRole?: string
  currentPage?: string
}

/**
 * BackofficeChat - Chat interface for logged-in users
 *
 * Features:
 * - Conversation history sidebar
 * - New conversation button
 * - Delete conversation functionality
 * - Role-aware system prompts
 * - Context based on user role and page
 * - Theme-aware styling matching backoffice
 */
export function BackofficeChat({ userRole, currentPage }: BackofficeChatProps) {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Load conversations on mount
  useEffect(() => {
    loadConversations()
  }, [])

  // Load messages when conversation changes
  useEffect(() => {
    if (activeConversationId) {
      loadMessages(activeConversationId)
    }
  }, [activeConversationId])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when component mounts
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch('/api/chat/backoffice/conversations', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Map backend response to frontend format
        const mappedConversations: Conversation[] = (data.conversations || []).map((conv: any) => ({
          id: conv.conversationId,
          title: conv.lastMessage,
          createdAt: new Date(conv.createdAt),
          updatedAt: new Date(conv.createdAt)
        }))
        setConversations(mappedConversations)

        // Set active conversation to most recent if none selected
        if (mappedConversations.length > 0 && !activeConversationId) {
          setActiveConversationId(mappedConversations[0].id)
        }
      }
    } catch (error) {
      console.error('Failed to load conversations:', error)
    }
  }

  const loadMessages = async (conversationId: string) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`/api/chat/backoffice/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Map backend response to frontend format
        const mappedMessages: Message[] = (data.messages || []).map((msg: any) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.createdAt)
        }))
        setMessages(mappedMessages)
      }
    } catch (error) {
      console.error('Failed to load messages:', error)
    }
  }

  const handleNewConversation = () => {
    const newConversationId = crypto.randomUUID()
    const newConversation: Conversation = {
      id: newConversationId,
      title: 'New Conversation',
      createdAt: new Date(),
      updatedAt: new Date()
    }

    setConversations(prev => [newConversation, ...prev])
    setActiveConversationId(newConversationId)
    setMessages([])

    // Close sidebar on mobile
    if (window.innerWidth < 768) {
      setSidebarOpen(false)
    }

    // Focus input
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation()

    if (!confirm('Are you sure you want to delete this conversation?')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`/api/chat/backoffice/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        setConversations(prev => prev.filter(c => c.id !== conversationId))

        // If deleted conversation was active, clear messages
        if (activeConversationId === conversationId) {
          setActiveConversationId(null)
          setMessages([])

          // Set active to next available conversation
          const remaining = conversations.filter(c => c.id !== conversationId)
          if (remaining.length > 0) {
            setActiveConversationId(remaining[0].id)
          }
        }
      } else {
        const errorData = await response.json()
        alert(errorData.error || 'Failed to delete conversation')
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      alert('Failed to delete conversation. Please try again.')
    }
  }

  const handleSendMessage = async () => {
    const message = inputValue.trim()
    if (!message || isLoading || !activeConversationId) return

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsLoading(true)
    setIsTyping(true)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token')
      }

      // Call backoffice chat API
      const response = await fetch('/api/chat/backoffice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message,
          conversationId: activeConversationId,
          context: {
            page: currentPage,
            feature: undefined // Feature can be derived from page path in backend
          }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to send message')
      }

      const data = await response.json()

      // Add assistant message
      const assistantMessage: Message = {
        id: data.messageId,
        role: 'assistant',
        content: data.message,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, assistantMessage])

      // Update conversation title to last message
      setConversations(prev =>
        prev.map(c =>
          c.id === activeConversationId
            ? { ...c, title: message.substring(0, 50), updatedAt: new Date() }
            : c
        )
      )
    } catch (error) {
      console.error('Chat error:', error)

      // Add error message
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again later.',
        timestamp: new Date()
      }

      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
      setIsTyping(false)
      inputRef.current?.focus()
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className="flex h-[calc(100vh-140px)] bg-card rounded-lg shadow-lg border border-border overflow-hidden">
      {/* Sidebar - Conversation History */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 300, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-muted border-r border-border flex flex-col"
          >
            {/* Sidebar Header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-foreground">Conversations</h3>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 hover:bg-muted rounded transition-colors"
                  aria-label="Close sidebar"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
              <button
                onClick={handleNewConversation}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </button>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {conversations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No conversations yet.<br />Start a new chat!
                </div>
              ) : (
                conversations.map((conversation) => (
                  <motion.button
                    key={conversation.id}
                    onClick={() => setActiveConversationId(conversation.id)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors group relative ${
                      activeConversationId === conversation.id
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-blue-600 dark:border-blue-500'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <MessageSquare className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      activeConversationId === conversation.id
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-muted-foreground'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        activeConversationId === conversation.id
                          ? 'text-foreground'
                          : 'text-foreground'
                      }`}>
                        {conversation.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {conversation.updatedAt.toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDeleteConversation(conversation.id, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-all"
                      aria-label="Delete conversation"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </motion.button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-muted">
          <div className="flex items-center gap-3">
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Open sidebar"
              >
                <MessageSquare className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
            <div>
              <h3 className="font-semibold text-foreground">
                {conversations.find(c => c.id === activeConversationId)?.title || 'Backoffice Assistant'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {userRole ? `Role: ${userRole.replace('_', ' ')}` : 'Logged in'}
              </p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-card">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🤖</span>
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Start a conversation
                </h4>
                <p className="text-sm text-muted-foreground">
                  I'm here to help you navigate the backoffice and answer your questions.
                </p>
              </div>
            </div>
          ) : (
            <>
              <AnimatePresence>
                {messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </AnimatePresence>

              {/* Typing Indicator */}
              <AnimatePresence>
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="flex gap-2"
                  >
                    <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3">
                      <div className="flex gap-1">
                        <TypingDot />
                        <TypingDot delay={0.15} />
                        <TypingDot delay={0.3} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border bg-muted">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={isLoading || !activeConversationId}
              className="flex-1 px-4 py-3 bg-background rounded-xl border border-input focus:ring-2 focus:ring-blue-500 dark:focus:ring-orange-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed text-foreground placeholder:text-muted-foreground"
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim() || !activeConversationId}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
              aria-label="Send message"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Powered by AI • Responses may vary
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * MessageBubble - Individual message component
 */
function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user'

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-tr-sm'
            : 'bg-muted text-foreground rounded-tl-sm'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        <p
          className={`text-xs mt-1 ${
            isUser ? 'text-white/70' : 'text-muted-foreground'
          }`}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      </div>
    </motion.div>
  )
}

/**
 * TypingDot - Animated typing indicator dot
 */
function TypingDot({ delay = 0 }: { delay?: number }) {
  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0.5 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{
        duration: 0.6,
        repeat: Infinity,
        repeatType: 'reverse',
        delay
      }}
      className="w-2 h-2 bg-muted-foreground rounded-full"
    />
  )
}
